import rootStore, { RootStore } from '@/app/store'
import { makeAutoObservable, runInAction } from 'mobx'
import {
  appendMessagesToThread,
  getChatStreamOptions,
  getToolStreamOptions,
} from '../tools/api'
import { MessageContentItem, Tool } from '../tools/types'
import { trackEvent } from '../tracking/api'
import { shallowAbortErrorOrThrow } from '@/utils/error'
import { queryClient } from '@/lib/react-query'

type SendMessageOptions = {
  message: string
  model?: string
  files?: File[]
  hiddenContent?: string
  systemMessageBuilder?: string
  expectStream?: boolean
  skipSearch?: boolean
  skipRAG?: boolean
  skipDatabase?: boolean
  projectId?: string
  deepResearch?: boolean
}

export class ToolStore {
  rootStore: RootStore
  data?: Tool
  inputs: any = {}
  generating = false
  awaitingFirstToken = false
  generationAbortController: AbortController | null = null

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  // NOTE: Called on logout
  public resetState() {
    this.maybeCancelGeneration()
    this.data = undefined
    this.inputs = {}
    this.generating = false
  }

  public setTool(tool: Tool) {
    this.data = tool
  }

  public setInputs(inputs: any) {
    this.inputs = inputs
  }

  public clearTool() {
    this.maybeCancelGeneration()
    this.data = undefined
    this.inputs = {}
  }

  trackToolGeneration() {
    const { chat } = rootStore

    if (chat.thread._id) {
      trackEvent({
        key: 'form_tool_re_generation',
        data: {
          toolId: this.data!.id,
          threadId: chat.thread._id,
        },
      })
    } else {
      trackEvent({
        key: 'form_tool_generation',
        data: {
          toolId: this.data!.id,
        },
      })
    }
  }

  async generate(expectStream = true, isDemo = false) {
    this.generating = true
    const { chat } = rootStore

    // NOTE: Track before clearing the thread
    this.trackToolGeneration()

    await chat.clearThread()

    if (!isDemo) {
      try {
        await chat.maybeCreateThread(this.data!.id)
      } catch (error) {
        return
      }
    }

    this.maybeCancelGeneration()
    this.generationAbortController = new AbortController()

    try {
      const streamOptions = getToolStreamOptions({
        tool: this.data!,
        signal: this.generationAbortController.signal,
        inputs: this.inputs,
        threadId: chat.thread._id,
        isDemo,
      })

      if (expectStream) {
        await chat.ingestMessageStream(streamOptions)
      }
    } catch (error) {
      const lastMessage = chat.thread.messages[chat.thread.messages.length - 1]

      if (lastMessage?.role === 'assistant') {
        // Remove any pending plugins from the message
        const filteredContent = lastMessage.content.filter(
          (content) =>
            !(content.type === 'pluginCall' && content.status === 'pending'),
        )

        // Check if there's any meaningful content left
        const hasTextContent = filteredContent.some(
          (content) =>
            content.type === 'text' &&
            content.text &&
            content.text.trim() !== '',
        )

        if (hasTextContent || filteredContent.length > 0) {
          // Keep the message but remove pending plugins
          runInAction(() => {
            lastMessage.content = filteredContent
          })
        } else {
          // Remove the entire message if nothing meaningful left
          runInAction(() => {
            chat.thread.messages.pop()
          })
        }
      }
      shallowAbortErrorOrThrow(error as Error)
    } finally {
      runInAction(() => {
        this.generating = false
      })
    }
  }

  async sendMessage({
    message,
    model,
    files,
    hiddenContent,
    systemMessageBuilder,
    expectStream = true,
    skipSearch = false,
    skipRAG = false,
    skipDatabase = false,
    projectId,
    deepResearch = false,
  }: SendMessageOptions) {
    this.generating = true
    const { chat } = rootStore
    chat.setSuggestions([])

    try {
      await chat.maybeCreateThread(this.data!.id, projectId)
    } catch (error) {
      return
    }

    // HACK! This MUST be called before adding user message. Otherwise, we accidentally inject an assistant message
    // with user cancellation (see function body)
    this.maybeCancelGeneration()

    const userMessageContent: MessageContentItem[] = [
      { type: 'text', text: message },
    ]

    if (hiddenContent) {
      userMessageContent.push({
        type: 'text',
        text: hiddenContent,
        isHidden: true,
      })
    }

    if (files && files.length > 0) {
      for (const file of files) {
        userMessageContent.unshift({
          type: 'file',
          filename: file.name,
        })
      }
    }

    runInAction(() => {
      this.awaitingFirstToken = true
    })
    chat.addMessage({
      role: 'user',
      content: userMessageContent,
    })

    this.generationAbortController = new AbortController()

    try {
      const streamOptions = getChatStreamOptions({
        id: this.data!.id,
        prompt: message,
        model,
        signal: this.generationAbortController.signal,
        threadId: chat.thread._id,
        systemMessageBuilder: systemMessageBuilder,
        hiddenContent,
        skipSearch,
        skipRAG,
        skipDatabase,
        files,
        projectId,
        deepResearch,
      })

      if (expectStream) {
        await chat.ingestMessageStream(streamOptions, () => {
          runInAction(() => {
            this.awaitingFirstToken = false
          })
        })

        const assistantMessages = chat.thread.messages.filter(
          (m) => m.role === 'assistant',
        )
        if (assistantMessages.length === 1) {
          // Esperar 2 segundos para que el backend termine de generar y guardar el título
          setTimeout(async () => {
            await queryClient.invalidateQueries(['recent-chats'])
            await queryClient.refetchQueries(['recent-chats'])
          }, 2000)
        }
      }
    } catch (error) {
      const lastMessage = chat.thread.messages[chat.thread.messages.length - 1]

      if (lastMessage?.role === 'assistant') {
        // Remove any pending plugins from the message
        const filteredContent = lastMessage.content.filter(
          (content) =>
            !(content.type === 'pluginCall' && content.status === 'pending'),
        )

        // Check if there's any meaningful content left
        const hasTextContent = filteredContent.some(
          (content) =>
            content.type === 'text' &&
            content.text &&
            content.text.trim() !== '',
        )

        if (hasTextContent || filteredContent.length > 0) {
          // Keep the message but remove pending plugins
          runInAction(() => {
            lastMessage.content = filteredContent
          })
        } else {
          // Remove the entire message if nothing meaningful left
          runInAction(() => {
            chat.thread.messages.pop()
          })
        }
      }
      shallowAbortErrorOrThrow(error as Error)
    } finally {
      runInAction(() => {
        this.generating = false
        this.awaitingFirstToken = false
      })
    }
  }

  public maybeCancelGeneration() {
    if (this.generationAbortController) {
      this.generationAbortController.abort()

      const chatThread = this.rootStore.chat.thread

      if (!chatThread._id || !chatThread || !this.awaitingFirstToken) {
        return
      }

      const lastMessage = chatThread.messages[chatThread.messages.length - 1]

      // SCENARIO 1: User cancelled generation before the first token was received
      if (lastMessage?.role === 'user') {
        const interruptedMessage = {
          role: 'assistant' as const,
          content: [
            { type: 'text' as const, text: 'Se ha cancelado la petición.' },
          ],
        }
        chatThread.messages.push(interruptedMessage)
        appendMessagesToThread(chatThread._id, [interruptedMessage])
        return
      }

      // TODO: SCENARIO 2: User cancelled generation after assistant message was received
      // This MUST be solved in backend or else we will duplicate the assistant message
      if (lastMessage?.role === 'assistant') {
        return
      }
    }
  }
}
